import React, {useEffect, useId, useRef, useState} from 'react';
import {Box, ButtonBase, IconButton, Typography} from '@mui/material';
import {ChevronLeft, ChevronRight} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {getSettingsColors} from '../settings/SettingsDesign';
import moment from 'moment-timezone';

const dateKey = date => date.format('YYYY-MM-DD');
const validDay = date => date && moment(date).isValid() ? moment(moment(date).format('YYYY-MM-DD'), 'YYYY-MM-DD', true) : null;

// Calendar dates are wall-clock dates. Never convert them through UTC or a JS Date.
export default function DateRangeCalendar({
                                              startDate, endDate, focusedInput = 'startDate', onDatesChange, onFocusChange,
                                              minDate, maxDate, today = moment(), numberOfMonths = 2, orientation = 'horizontal', daySize = 39
                                          }) {
    const start = validDay(startDate);
    const end = validDay(endDate);
    const minimum = validDay(minDate);
    const maximum = validDay(maxDate);
    const currentDay = validDay(today);
    const initialDay = start || validDay(today) || moment().startOf('day');
    const [month, setMonth] = useState(() => initialDay.clone().startOf('month'));
    const [activeDate, setActiveDate] = useState(() => dateKey(initialDay));
    const [hoverDate, setHoverDate] = useState(null);
    const calendarRef = useRef(null);
    const pendingFocus = useRef(false);
    const titleId = useId();
    const selectingEnd = focusedInput === 'endDate' && !!start;
    const previewEnd = selectingEnd && hoverDate && !moment(hoverDate).isBefore(start, 'day') ? moment(hoverDate) : end;
    const firstWeekday = moment.localeData().firstDayOfWeek();
    const months = Array.from({length: numberOfMonths}, (_, index) => month.clone().add(index, 'month'));
    const isDisabled = day => !!((minimum && day.isBefore(minimum, 'day')) || (maximum && day.isAfter(maximum, 'day')));
    const previousDisabled = !!minimum && month.clone().subtract(1, 'month').endOf('month').isBefore(minimum, 'day');
    const nextDisabled = !!maximum && month.clone().add(numberOfMonths, 'month').isAfter(maximum, 'month');

    useEffect(() => {
        if (pendingFocus.current) {
            calendarRef.current?.querySelector(`[data-date="${activeDate}"]`)?.focus();
            pendingFocus.current = false;
        }
    }, [activeDate, month]);

    const focusDay = requestedDay => {
        let day = requestedDay.clone();
        if (minimum && day.isBefore(minimum, 'day')) day = minimum.clone();
        if (maximum && day.isAfter(maximum, 'day')) day = maximum.clone();
        if (day.isBefore(month, 'month')) setMonth(day.clone().startOf('month'));
        else if (day.isAfter(month.clone().add(numberOfMonths - 1, 'month'), 'month')) {
            setMonth(day.clone().subtract(numberOfMonths - 1, 'month').startOf('month'));
        }
        pendingFocus.current = true;
        setActiveDate(dateKey(day));
        setHoverDate(dateKey(day));
    };

    const changeMonth = offset => {
        const nextMonth = month.clone().add(offset, 'month');
        setMonth(nextMonth);
        let nextActive = nextMonth.clone().date(Math.min(moment(activeDate).date(), nextMonth.daysInMonth()));
        if (minimum && nextActive.isBefore(minimum, 'day')) nextActive = minimum.clone();
        if (maximum && nextActive.isAfter(maximum, 'day')) nextActive = maximum.clone();
        setActiveDate(dateKey(nextActive));
        setHoverDate(null);
    };

    const selectDay = day => {
        if (isDisabled(day)) return;
        setActiveDate(dateKey(day));
        setHoverDate(null);
        if (!selectingEnd || day.isBefore(start, 'day')) {
            onDatesChange({startDate: day.clone(), endDate: null});
            onFocusChange?.('endDate');
        } else {
            onDatesChange({startDate: start.clone(), endDate: day.clone()});
            onFocusChange?.('startDate');
        }
    };

    const handleKeyDown = (event, day) => {
        const weekday = (day.day() - firstWeekday + 7) % 7;
        const moves = {
            ArrowLeft: () => day.clone().subtract(1, 'day'),
            ArrowRight: () => day.clone().add(1, 'day'),
            ArrowUp: () => day.clone().subtract(7, 'day'),
            ArrowDown: () => day.clone().add(7, 'day'),
            Home: () => day.clone().subtract(weekday, 'day'),
            End: () => day.clone().add(6 - weekday, 'day'),
            PageUp: () => day.clone().subtract(1, event.shiftKey ? 'year' : 'month'),
            PageDown: () => day.clone().add(1, event.shiftKey ? 'year' : 'month')
        };
        if (moves[event.key]) {
            event.preventDefault();
            focusDay(moves[event.key]());
        }
    };

    // A saved range may precede the minimum; keep a selectable day in the tab order.
    const enabledDates = months.flatMap(currentMonth => Array.from({length: currentMonth.daysInMonth()}, (_, index) => currentMonth.clone().date(index + 1))).filter(day => !isDisabled(day));
    const tabDate = enabledDates.some(day => dateKey(day) === activeDate) ? activeDate : enabledDates[0] && dateKey(enabledDates[0]);

    const navigationStyles = {
        width: 30, height: 30, borderRadius: '8px', color: 'text.secondary',
        bgcolor: theme => getSettingsColors(theme).inset,
        '&:hover': {color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.1)}
    };

    return <Box ref={calendarRef} aria-label="Date range calendar" sx={{
        width: orientation === 'vertical' ? daySize * 7 + 16 : numberOfMonths * (daySize * 7 + 16),
        maxWidth: '100%', color: 'text.primary'
    }}>
        <Box sx={{display: 'grid', gridTemplateColumns: orientation === 'vertical' ? 'minmax(0, 1fr)' : `repeat(${numberOfMonths}, minmax(0, 1fr))`}}>
            {months.map((currentMonth, monthIndex) => {
                const offset = (currentMonth.day() - firstWeekday + 7) % 7;
                const weeks = numberOfMonths === 1 ? Math.ceil((offset + currentMonth.daysInMonth()) / 7) : 6;
                return <Box key={currentMonth.format('YYYY-MM')} sx={{px: 1, minWidth: 0}}>
                    <Box sx={{display: 'grid', gridTemplateColumns: '30px minmax(0, 1fr) 30px', alignItems: 'center', mb: 2}}>
                        {monthIndex === 0 ? <IconButton aria-label="Previous month" disabled={previousDisabled} onClick={() => changeMonth(-1)} size="small" sx={navigationStyles}>
                            <ChevronLeft fontSize="small"/>
                        </IconButton> : <Box/>}
                        <Typography id={`${titleId}-${monthIndex}`} component="h3" sx={{fontSize: 14, fontWeight: 600, textAlign: 'center'}}>
                            {currentMonth.format('MMMM YYYY')}
                        </Typography>
                        {monthIndex === months.length - 1 ? <IconButton aria-label="Next month" disabled={nextDisabled} onClick={() => changeMonth(1)} size="small" sx={navigationStyles}>
                            <ChevronRight fontSize="small"/>
                        </IconButton> : <Box/>}
                    </Box>
                    <Box role="grid" aria-labelledby={`${titleId}-${monthIndex}`} aria-multiselectable="true" onMouseLeave={() => setHoverDate(null)}>
                        <Box role="row" sx={{display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', mb: 1}}>
                            {Array.from({length: 7}, (_, index) => moment().day((firstWeekday + index) % 7)).map(day => <Typography
                                key={day.day()} role="columnheader" aria-label={day.format('dddd')} sx={{fontSize: 11, fontWeight: 500, textAlign: 'center', color: 'text.secondary'}}>
                                {day.format('dd')}
                            </Typography>)}
                        </Box>
                        {Array.from({length: weeks}, (_, week) => <Box key={week} role="row" sx={{display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', py: 0.25}}>
                            {Array.from({length: 7}, (_, weekday) => {
                                const dayNumber = week * 7 + weekday - offset + 1;
                                if (dayNumber < 1 || dayNumber > currentMonth.daysInMonth()) return <Box key={weekday} role="gridcell" sx={{height: daySize}}/>;
                                const day = currentMonth.clone().date(dayNumber);
                                const isStart = !!start && day.isSame(start, 'day');
                                const isEnd = !!end && day.isSame(end, 'day');
                                const selected = isStart || isEnd;
                                const inRange = !!(start && previewEnd && day.isBetween(start, previewEnd, 'day', '[]'));
                                const rangeEnd = !!previewEnd && day.isSame(previewEnd, 'day');
                                const isToday = day.isSame(currentDay, 'day');
                                return <Box key={weekday} role="gridcell" aria-selected={selected || inRange} sx={{
                                    display: 'flex', justifyContent: 'center', height: daySize,
                                    bgcolor: theme => inRange ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                    borderRadius: isStart && rangeEnd ? '50%' : isStart ? '20px 0 0 20px' : rangeEnd ? '0 20px 20px 0' : 0
                                }}>
                                    <ButtonBase type="button" data-date={dateKey(day)} disabled={isDisabled(day)}
                                        tabIndex={dateKey(day) === tabDate ? 0 : -1}
                                        aria-label={day.format('dddd, D MMMM YYYY')} aria-pressed={selected}
                                        aria-current={isToday ? 'date' : undefined}
                                        onClick={() => selectDay(day)} onKeyDown={event => handleKeyDown(event, day)}
                                        onFocus={() => setActiveDate(dateKey(day))} onMouseEnter={() => setHoverDate(dateKey(day))}
                                        sx={{width: '100%', maxWidth: daySize, height: daySize, borderRadius: '50%', fontSize: 13,
                                            bgcolor: selected ? 'primary.main' : 'transparent',
                                            color: theme => selected ? (theme.palette.mode === 'dark' ? theme.palette.background.darkBlue : theme.palette.primary.contrastText) : theme.palette.text.primary,
                                            fontWeight: selected || isToday ? 600 : 400,
                                            '&:hover': {bgcolor: theme => selected ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.12)},
                                            '&.Mui-disabled': {color: 'text.disabled'},
                                            '&.Mui-focusVisible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1, zIndex: 1},
                                            ...(isToday && {'&::after': {content: '\'\'', position: 'absolute', bottom: 4, width: 3, height: 3, borderRadius: '50%', bgcolor: theme => selected ? (theme.palette.mode === 'dark' ? theme.palette.background.darkBlue : theme.palette.primary.contrastText) : theme.palette.primary.main}})
                                        }}>
                                        {dayNumber}
                                    </ButtonBase>
                                </Box>;
                            })}
                        </Box>)}
                    </Box>
                </Box>;
            })}
        </Box>
        <Typography role="status" aria-live="polite" sx={{fontSize: 12, color: 'text.secondary', textAlign: 'center', mt: 1.5}}>
            {selectingEnd ? 'Choose an end date to complete your range' : 'Choose a start date for your range'}
        </Typography>
    </Box>;
}
